"use client";

import Image from "next/image";
import Section from "../../../components/custom/section";
import { ReactNode } from "react";

export default function CompanyValuesSection() {
  return (
    <Section className="py-16 md:py-24 lg:py-32 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <div className="relative z-10 flex flex-col items-center gap-4 md:gap-6 mb-12 md:mb-20">
        <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide">
          Our Foundation
        </span>
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground text-center">
          Values
        </h2>
        <p className="text-base md:text-lg text-muted-foreground max-w-xl text-center leading-relaxed">
          Reliability, quality, and efficiency drive our business.
        </p>
      </div>

      {/* Cards */}
      <div className="relative z-10 space-y-6 md:space-y-8">
        <CompanyValueCard
          align="left"
          number="01"
          title="Reliability"
          description="Certified European spare parts with consistent quality and dependable supply."
          image="/images/who-we-are/reliability.png"
        />
        <CompanyValueCard
          align="right"
          number="02"
          title="Quality"
          description="Products sourced mainly from Italy and Germany, known worldwide for engineering excellence."
          image="/images/who-we-are/quality.png"
        />
        <CompanyValueCard
          align="left"
          number="03"
          title="Efficiency"
          description="Competitive prices, quick logistics, and professional customer service in the UAE."
          image="/images/who-we-are/efficiency.png"
        />
      </div>
    </Section>
  );
}

type CompanyValueCardProps = {
  readonly align?: "left" | "right";
  readonly number: string;
  readonly title: ReactNode;
  readonly description: string;
  readonly image: string;
};

function CompanyValueCard({
  align = "left",
  number,
  title,
  description,
  image,
}: CompanyValueCardProps) {
  const isLeft = align === "left";

  return (
    <div className="group relative w-full rounded-2xl md:rounded-3xl overflow-hidden bg-card border border-border/30 hover:border-primary/20 transition-all duration-500">
      {/* Mobile: Stacked layout | Desktop: Side by side */}
      <div
        className={`flex flex-col ${isLeft ? "md:flex-row" : "md:flex-row-reverse"}`}
      >
        {/* Image Section */}
        <div className="relative w-full md:w-1/2 aspect-[4/3] md:aspect-auto md:min-h-[400px] lg:min-h-[500px] overflow-hidden">
          <Image
            src={image}
            alt={String(title)}
            fill
            className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        </div>

        {/* Content Section */}
        <div
          className={`relative w-full md:w-1/2 p-6 md:p-10 lg:p-14 flex flex-col justify-center ${isLeft ? "md:items-start md:text-left" : "md:items-end md:text-right"} items-center text-center`}
        >
          {/* Number Badge */}
          <div className="mb-4 md:mb-6">
            <span className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/10 border border-primary/20 text-primary font-bold text-lg md:text-xl transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground group-hover:scale-110">
              {number}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4 transition-colors duration-300 group-hover:text-primary">
            {title}
          </h3>

          {/* Description */}
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
            {description}
          </p>

          {/* Decorative line */}
          <div
            className={`mt-6 md:mt-8 h-1 w-0 bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500 ease-out group-hover:w-24`}
          />
        </div>
      </div>

      {/* Subtle corner accent */}
      <div
        className={`absolute ${isLeft ? "top-0 right-0" : "top-0 left-0"} w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}
      />
    </div>
  );
}
